import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import JobService from "../../api/JobService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import { MaterialsList } from "../../components/MaterialsList/MaterialsList";
import Modal from "../../components/Modal/Modal";
import NoProductDocumentsModal from "../../components/Modal/NoProductDocumentsModal";
import { NoDocumentation } from "../../components/NoDocumentation/NoDocumentation";
import { PropertyAreaMonitors } from "../../components/PropertyAreaMonitors/PropertyAreaMonitors";
import { TechniciansList } from "../../components/TechniciansList/TechniciansList";
import { TimeTrackingOverview } from "../../components/TimeTrackingOverview/TimeTrackingOverview";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useDownloadFiles } from "../../hooks/useDownloadFiles";
import { useScreenSize } from "../../hooks/useScreenSize";
import { useUnauthorizedAccess } from "../../hooks/useUnauthorizedAccess";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { splitDateTime } from "../../services/DateTimeService";
import { RootState } from "../../store";
import { appStateActions } from "../../store/app-state-slice";
import { appointmentsActions } from "../../store/appointments-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/services-overview.module.css";
import { calculateTotalTime } from "../../utils/calculateTotalTime";
import { mapPropertyAreasWithMonitors } from "../../utils/mapPropertyAreas";

export const ServicesOverviewReadonly = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDesktop } = useScreenSize();
  const { isCustomerAdmin } = useSelector((state: RootState) => state.user);

  const [jobId, setJobId] = useState("");
  const [propertyAreaMonitors, setPropertyAreaMonitors] = useState<any[]>([]);
  const [webProducts, setWebProducts] = useState<any>(null);
  const [sevdeskProducts, setSevdeskProducts] = useState<any>(null);
  const [products, setProducts] = useState<any>(null);
  const [infestationCountPerArea, setInfestationCountPerArea] = useState<{
    [key: string]: number;
  }>({});
  const [loading, setLoading] = useState(true);
  const [lastEventDate, setLastEventDate] = useState<string | null>(null);

  const serviceStarted = useSelector(
    (state: RootState) => state.clock.isRunning
  );
  const servicesStep = useSelector(
    (state: RootState) => state.tabs.services.step
  );
  const isOnline = useSelector((state: RootState) => state.online.isOnline);

  const openAppointment = useSelector((state: RootState) => state.appointment);
  const appointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );

  const isEmpty = useSelector(
    (state: RootState) => state.technicianDocuments.empty
  );
  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  useUnauthorizedAccess(appointment);
  const { fetchDocumentation, requestLoading, noDocument, setNoDocument } =
    useDocumentation();

  const { closeModal } = useDownloadFiles();

  useEffect(() => {
    if (appointment.jobs.length > 0) {
      const copiedJobs = Array.from(appointment.jobs);
      const lastJobId = copiedJobs.pop();
      setJobId(lastJobId.id);
    }
  }, [appointment.jobs]);

  useEffect(() => {
    const fetchLastEvent = async () => {
      if (!appointment.id) return;

      const result = await calculateTotalTime(appointment.id);

      if (result.lastEvent?.createdAt) {
        const date = splitDateTime(result.lastEvent.createdAt).date;
        setLastEventDate(date);
      }
    };

    fetchLastEvent();
  }, [appointment.id]);

  const fetchPropertyAreaMonitors = useCallback(async () => {
    if (!appointment.propertyId || !appointment.id) {
      return;
    }
    if (appointment.propertyId) {
      try {
        dispatch(activeTabsActions.setMonitorsStep(1));

        const [propertyResponse, propertyAreasResponse] = await Promise.all([
          PropertyService.GetProperty(appointment.propertyId),
          AppointmentService.GetAppointmentPropertyAreasWithVersions(
            appointment.id
          ),
        ]);

        const property = propertyResponse.data;
        const propertyAreasWithVersions = propertyAreasResponse.data;

        dispatch(appStateActions.addProperty(property));

        const propertyAreas = (propertyAreasWithVersions || []).filter(
          (area: any) =>
            Array.isArray(area.monitors) && area.monitors.length > 0
        );

        const propertyAreaIds = propertyAreas.map((area: any) => area.id);
        const monitorsPromises = propertyAreaIds.map((propertyAreaId: any) =>
          AppointmentService.GetMonitorsWithVersions(
            appointment.id,
            propertyAreaId
          )
        );

        const monitorsResponses = await Promise.all(monitorsPromises);

        const monitorsData = monitorsResponses.map((response) =>
          response.data.filter(
            (monitor: any) => monitor.versions && monitor.versions.length > 0
          )
        );

        setPropertyAreaMonitors(
          mapPropertyAreasWithMonitors(propertyAreas, monitorsData)
        );

        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    }
  }, [dispatch, appointment.propertyId, appointment.id]);

  const fetchGroupedMaterialsByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } = await JobService.GetGroupedMaterialsByJobPDF(jobId);

        if (data.webProducts) {
          setWebProducts(
            data.webProducts.map((p: any) => ({
              id: p.item.productId,
              name: p.item.product.name,
              quantity: p.quantity,
              description: p.item.product.description,
              payExtraQuantity: p.payExtraQuantity ?? 0,
            }))
          );
        }

        if (data.sevdeskProducts) {
          setSevdeskProducts(
            data.sevdeskProducts.map((p: any) => ({
              id: p.item.id,
              name: p.item.name,
              quantity: p.quantity,
              description: p.description,
              payExtraQuantity: p.payExtraQuantity ?? 0,
            }))
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId]);

  const fetchGroupedEventsByJob = useCallback(async () => {
    if (jobId) {
      try {
        const { data } = await JobService.GetGroupedEventsByJob(jobId);

        const infestationCountMap: { [key: string]: number } = {};

        data.forEach((areaData: any) => {
          const areaLabel = areaData.label;
          const infestationEvents = areaData.events.infestation;

          if (infestationEvents) {
            infestationCountMap[areaLabel] = infestationEvents.length;
          } else {
            infestationCountMap[areaLabel] = 0;
          }
        });

        setInfestationCountPerArea(infestationCountMap);
      } catch (error) {
        console.log(error);
      }
    }
  }, [jobId]);

  useEffect(() => {
    if (isOnline) {
      fetchPropertyAreaMonitors();
      fetchGroupedMaterialsByJob();
      fetchGroupedEventsByJob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchGroupedEventsByJob,
    fetchGroupedMaterialsByJob,
    fetchPropertyAreaMonitors,
    isOnline,
    appointment,
  ]);

  useEffect(() => {
    const combinedProducts = [
      ...(webProducts || []),
      ...(sevdeskProducts || []),
    ];

    setProducts(combinedProducts);
  }, [webProducts, sevdeskProducts]);

  const handleBackButtonClicked = () => {
    if (isViewOnly) {
      navigate(`/properties/${appointment.propertyId}/viewonly`);
    }
    if (servicesStep === 1 && !isViewOnly) {
      if (isCustomerAdmin) {
        navigate("/");
      } else {
        dispatch(overviewAppointmentSliceActions.resetState());
        dispatch(servicedMonitorsActions.resetState());
        navigate(`/properties/${openAppointment.id}/show`);
      }
    } else {
      dispatch(activeTabsActions.setServicesStep(1));
    }
  };

  const fetchDocument = () => {
    fetchDocumentation(appointment.document?.url);
  };

  useEffect(() => {
    return () => {
      dispatch(
        appointmentsActions.setAppointments({
          today: [],
          tomorrow: [],
          future: [],
        })
      );
    };
  });

  const propertyAreaMonitorsSortedByInfestationCount = useMemo(() => {
    return [...propertyAreaMonitors].sort((a, b) => {
      const infestationCountA = infestationCountPerArea[a.propertyArea] || 0;
      const infestationCountB = infestationCountPerArea[b.propertyArea] || 0;
      return infestationCountB - infestationCountA;
    });
  }, [propertyAreaMonitors, infestationCountPerArea]);

  return (
    <AppLayout hideHeader={!isCustomerAdmin}>
      <div>
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          handleFetchDocument={fetchDocument}
        />
        <AppointmentDetails isServicesOverview lastEventDate={lastEventDate} />

        <Modal
          isOpen={isEmpty}
          message={t("NoInformation")}
          note={"NoAvailableDocuments"}
          iconType="noInfo"
          confirmText={"Back"}
          onConfirm={closeModal}
        />

        <NoProductDocumentsModal />

        {servicesStep === 1 && loading && <LoadingSpinner loading={loading} />}

        {!loading && jobId && (
          <div className={styles["content-wrapper"]}>
            <TimeTrackingOverview appointmentId={appointment.id} />

            <TechniciansList technicians={appointment.technicians} />

            <div className={styles.materials}>
              <CustomInput
                label={t("Remark")}
                placeholder=""
                hideTick
                initialValue={appointment.comment ?? ""}
                readonly
              />
            </div>

            <div className={styles.materials}>
              <MaterialsList
                title={"Materialeinsatz"}
                data={products}
                readOnly={true}
              />
            </div>

            <div className={styles.monitors}>
              <PropertyAreaMonitors
                readonly
                monitors={propertyAreaMonitorsSortedByInfestationCount}
                startService={() => {}}
                isServiceStarted={serviceStarted}
                isServicesOverview={true}
                jobId={jobId}
                infestationCountPerArea={infestationCountPerArea}
              />
            </div>

            {!isDesktop && (
              <FloatingButtons justify="right">
                <CustomButton
                  color="gray"
                  icon="document"
                  width={48}
                  loading={requestLoading}
                  onClick={fetchDocument}
                />
              </FloatingButtons>
            )}
          </div>
        )}

        {!loading && !jobId && (
          <>
            <NoDocumentation />

            <FloatingButtons>
              <CustomButton
                color="gray"
                {...(!isDesktop
                  ? { icon: "leftarrow", iconPosition: "left" }
                  : {})}
                width={70}
                onClick={handleBackButtonClicked}
              >
                {t("Back to overview")}
              </CustomButton>
            </FloatingButtons>
          </>
        )}
      </div>

      <Modal
        isOpen={noDocument}
        message={t("NoDocument")}
        confirmText="OK"
        iconType="alert"
        onConfirm={() => setNoDocument(false)}
      />
    </AppLayout>
  );
};
